<template>
    <InfoBaseForm
        ref="baseFormRef"
        v-model="formData"
        :is-loading="isLoading"
        :is-show-validation-error="
            isShowVallidationError ||
            hasAmenityValidationErrors ||
            !hasUserInteractedWithAmenities ||
            !hasValidAmenitySelection
        "
        data-cy="info-room-types-form"
        @save="validateForm"
        @cancel="emit('cancel')"
    >
        <template v-slot:name-block>
            <RoomBasicInfo
                v-model="formData"
                :service-type="serviceType"
                :categories="categories"
                :is-field-readonly="isFieldReadonly"
                :is-field-disabled="isFieldDisabled"
                :base-form-ref="baseFormRef"
                :is-hotel-description-mode="isHotelDescriptionMode"
                @update-capacity="updateCapacity"
            />
        </template>
        <template v-slot:center-block>
            <RoomAccommodationCategory
                v-model="formData"
                :service-type="serviceType"
                :categories="categories"
                :is-field-readonly="isFieldReadonly"
                :is-field-disabled="isFieldDisabled"
                @update-capacity="updateCapacity"
            />
            <RoomShortDescription
                v-model="formData"
                :base-form-ref="baseFormRef"
                :is-hotel-description-mode="isHotelDescriptionMode"
            />
        </template>
        <template v-slot:bottom-block>
            <RoomCapacityAndBeds
                v-model="formData"
                :bed-types="bedTypes"
                :is-field-readonly="isFieldReadonly"
                :is-field-disabled="isFieldDisabled"
                :is-hotel-description-mode="isHotelDescriptionMode"
                :is-special-room-type="isSpecialRoomType"
                :is-show-validation-error="isShowVallidationError"
                @update-min-total-occupancy="updateMinTotalOccupancy"
                @add-bed-row="addBedRow"
                @remove-bed-row="removeBedRow"
                @remove-alternate-bed-types="removeAlternateBedTypes"
                @add-row="addRow"
                @remove-row="removeRow"
                @update-children-ages-row="updateChildrenAgesRow"
            />

            <!-- Amenity Validation Errors Display -->
            <div v-if="amenityValidationErrors.length > 0" class="mb-4">
                <v-alert type="error" variant="outlined" class="mb-2">
                    <div class="font-weight-medium mb-2">{{ $t('room_types.amenity_validation_errors') }}</div>
                    <ul class="mb-0">
                        <li v-for="error in amenityValidationErrors" :key="error.token" class="mb-1">
                            {{ error.message }}
                        </li>
                    </ul>
                </v-alert>
            </div>

            <!-- Sustainable Room Amenities Section -->
            <SustainableRoomAmenities
                v-if="formData.sustainabilityLevel === 'SUSTAINABLE'"
                v-model="formData"
                :sustainable-amenities-data="sustainableAmenitiesData"
                :sustainable-amenities-loading="sustainableAmenitiesLoading"
                :sustainable-amenity-categories="sustainableAmenityCategories"
                :grouped-sustainable-amenities="groupedSustainableAmenities"
                :category-names="categoryNames"
                :is-field-readonly="isFieldReadonly"
                :get-amenity-value="getAmenityValue"
                :set-amenity-value="setAmenityValue"
                :set-amenity-kw-consumption="setAmenityKwConsumption"
                :cycle-energy-type="cycleEnergyType"
                :localized="localized"
                :get-amenity-type-constraints="getAmenityTypeConstraints"
                :validate-amenity-input="validateAmenityInput"
                :get-amenity-validation-message="getAmenityValidationMessage"
            />

            <!-- Zero Room Amenities Section -->
            <ZeroRoomAmenities
                v-if="formData.sustainabilityLevel === 'ZERO'"
                v-model="formData"
                :sustainable-amenities-data="sustainableAmenitiesData"
                :sustainable-amenities-loading="sustainableAmenitiesLoading"
                :zero-amenity-categories-basic="zeroAmenityCategoriesBasic"
                :zero-amenity-categories-extra="zeroAmenityCategoriesExtra"
                :grouped-zero-amenities-basic="groupedZeroAmenitiesBasic"
                :grouped-zero-amenities-extra="groupedZeroAmenitiesExtra"
                :category-names="categoryNames"
                :is-field-readonly="isFieldReadonly"
                :get-amenity-value="getAmenityValue"
                :set-amenity-value="setAmenityValue"
                :set-amenity-kw-consumption="setAmenityKwConsumption"
                :cycle-energy-type="cycleEnergyType"
                :localized="localized"
                :get-amenity-type-constraints="getAmenityTypeConstraints"
                :validate-amenity-input="validateAmenityInput"
                :get-amenity-validation-message="getAmenityValidationMessage"
            />

            <!-- Regular Hotel Room Amenities Section (for BASIC rooms only) -->
            <RoomAmenities
                v-if="amenityCategories.length > 0 && formData.sustainabilityLevel === 'BASIC'"
                v-model="formData"
                :amenity-categories="amenityCategories"
                :grouped-amenities="groupedAmenities"
                :category-names="categoryNames"
                :is-field-readonly="isFieldReadonly"
                :localized="localized"
            />

            <!-- Commission and Charges Block -->
            <CommissionBlock :commission-percentage="commissionPercentage" :calculated-earnings="calculatedEarnings" />

            <!-- Warning messages for BASIC room types -->
            <div v-if="formData.sustainabilityLevel === 'BASIC' && showBasicRoomWarnings" class="mt-4">
                <v-alert v-if="!hasEnoughEcoAmenitiesForSustainable" type="warning" variant="outlined" class="mb-2">
                    {{ $t('warning_message.insufficient_eco_amenities_for_sustainable') }}
                </v-alert>
                <v-alert v-if="!hasEnoughAmenitiesForZero" type="warning" variant="outlined" class="mb-2">
                    {{ $t('warning_message.insufficient_amenities_for_zero') }}
                </v-alert>
            </div>
            <template v-if="formData.sustainabilityLevel === 'BASIC'">
                <div class="text-subtitle-1 font-weight-medium pb-4">
                    {{ $t('channel_managers') }}
                </div>
                <ChannelManagersConfig
                    v-model="channelManagersConfigItems"
                    :is-loading="isLoadingChannelManagersConfig"
                    class="mb-10"
                />
            </template>
        </template>
    </InfoBaseForm>
</template>

<script setup lang="ts">
    import InfoBaseForm from '~src/components/products/information/InfoBaseForm.vue'
    import RoomBasicInfo from './RoomBasicInfo.vue'
    import RoomAccommodationCategory from './RoomAccommodationCategory.vue'
    import RoomShortDescription from './RoomShortDescription.vue'
    import RoomCapacityAndBeds from './RoomCapacityAndBeds.vue'
    import SustainableRoomAmenities from './SustainableRoomAmenities.vue'
    import ZeroRoomAmenities from './ZeroRoomAmenities.vue'
    import RoomAmenities from './RoomAmenities.vue'
    import CommissionBlock from './CommissionBlock.vue'
    import ChannelManagersConfig from './ChannelManagersConfig.vue'
    import {nanoid} from 'nanoid'
    import {initRoomType} from '~/utils/types/account/products/helpers'
    import {RoomCode} from '~/utils/types/types'
    import {useAmenitiesManagement} from '../../../composables/useAmenitiesManagement'
    import {useCommissionCalculation} from '~/composables/useCommissionCalculation'
    import {useRoomTypeForm} from '~/composables/useRoomTypeForm'

    const supplierStore = useSupplierStore()
    const app = useNuxtApp()
    const {t} = useI18n()

    const props = defineProps<{
        serviceType?: string
        service: object | null
        bedTypes: any[]
        categories: any[]
    }>()

    const formData = ref<any>(clone(props.service) || initRoomType())
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)

    // Use composables
    const {
        isLoading,
        isShowVallidationError,
        baseFormRef,
        localized,
        updateMinTotalOccupancy,
        addRow,
        removeRow,
        updateChildrenAgesRow,
        addBedRow,
        removeBedRow,
        removeAlternateBedTypes,
        isFieldReadonly,
        isFieldDisabled,
        isSpecialRoomType,
        useRoomServices,
        hiddenAmenities,
    } = useRoomTypeForm(formData, props)

    // Override updateCapacity to remove family room type capacity override
    const updateCapacity = () => {
        let capacity
        switch (formData.value.otaCode) {
            case RoomCode.SINGLE:
                capacity = 1
                break
            case RoomCode.DOUBLE:
                capacity = 2
                break
            case RoomCode.TRIPLE:
                capacity = 3
                break
            case RoomCode.QUADRUPLE:
            case RoomCode.FAMILY:
                capacity = 4
                break
            default:
                capacity = 0
        }
        if (capacity) {
            formData.value.minOccupancy = capacity
            formData.value.maxOccupancy = capacity
            formData.value.maxExtraPlace = 0
            formData.value.maxWithoutPlace = 0
            formData.value.minTotalOccupancy = capacity
            formData.value.maxTotalOccupancy = capacity
        }
    }

    const {
        amenitiesData,
        sustainableAmenitiesData,
        sustainableAmenitiesLoading,
        amenityModifications,
        categoryNames,
        getAmenityValue,
        setAmenityValue,
        setAmenityKwConsumption,
        convertToNumber,
        cycleEnergyType,
        getSafeKwConsumption,
        initializeAmenityEnergyData,
        initializeAmenityDefaults,
        groupedAmenities,
        amenityCategories,
        groupedSustainableAmenities,
        sustainableAmenityCategories,
        groupedZeroAmenitiesBasic,
        zeroAmenityCategoriesBasic,
        groupedZeroAmenitiesExtra,
        zeroAmenityCategoriesExtra,
    } = useAmenitiesManagement(t)

    // Amenity validation errors from server
    const amenityValidationErrors = ref([])

    // Function to get amenity type validation constraints
    const getAmenityTypeConstraints = (amenityTypeId: string | number) => {
        const amenityType = amenitiesData.value.find(a => a.id === amenityTypeId)
        if (!amenityType) return null

        return {
            minUsageHours: amenityType.minUsageHours,
            maxUsageHours: amenityType.maxUsageHours,
            minKWConsumption: amenityType.minKWConsumption,
            maxKWConsumption: amenityType.maxKWConsumption,
            defaultUsageHours: amenityType.usageHours,
            defaultKWConsumption: amenityType.kwconsumption,
        }
    }

    // Function to validate amenity input values
    const validateAmenityInput = (amenityTypeId: string | number, field: string, value: any) => {
        const constraints = getAmenityTypeConstraints(amenityTypeId)

        // For price field, check if it's required and greater than 0
        if (field === 'price') {
            if (!value || parseFloat(value) <= 0) return false
            return true
        }

        // For other fields, check if value is null, undefined, or empty string (but allow 0)
        if (value === null || value === undefined || value === '') return false

        if (!constraints) return true

        const numValue = parseFloat(value)
        if (isNaN(numValue)) return false

        if (field === 'usageHours') {
            return numValue >= constraints.minUsageHours && numValue <= constraints.maxUsageHours
        } else if (field === 'kwconsumption') {
            return numValue >= constraints.minKWConsumption && numValue <= constraints.maxKWConsumption
        }

        return true
    }

    // Function to get validation error message for amenity input
    const getAmenityValidationMessage = (amenityTypeId: string | number, field: string) => {
        const constraints = getAmenityTypeConstraints(amenityTypeId)

        if (field === 'price') {
            return t('room_types.price_required_greater_than_zero')
        }

        if (!constraints) return ''

        if (field === 'usageHours') {
            return t('room_types.usage_hours_validation', {
                min: constraints.minUsageHours,
                max: constraints.maxUsageHours,
            })
        } else if (field === 'kwconsumption') {
            return t('room_types.energy_consumption_validation', {
                min: constraints.minKWConsumption,
                max: constraints.maxKWConsumption,
            })
        }

        return ''
    }

    // Sum of basic-room amenity prices the operator has excluded for the current variant.
    // For SUSTAINABLE rooms a checked entry in sustainableAmenitiesEnabled means the
    // amenity is removed; for ZERO it's the same for the basicEnabled-true block. Only
    // basicEnabled=true amenities can be subtracted (the extras block on ZERO is purely
    // additive and doesn't lower the basic price).
    const excludedAmenityPriceSum = computed(() => {
        if (!sustainableAmenitiesData.value?.length) return 0

        const level = formData.value?.sustainabilityLevel
        if (level !== 'SUSTAINABLE' && level !== 'ZERO') return 0

        const checkedMap =
            level === 'SUSTAINABLE' ? formData.value?.sustainableAmenitiesEnabled : formData.value?.zeroAmenitiesEnabled
        if (!checkedMap) return 0

        return sustainableAmenitiesData.value.reduce((total, amenity) => {
            if (amenity.basicEnabled !== true) return total
            const amenityKey = amenity.amenityTypeId || amenity.id
            if (!checkedMap[amenityKey]) return total
            const price = parseFloat(getAmenityValue(amenityKey, 'price'))
            return total + (Number.isFinite(price) ? price : 0)
        }, 0)
    })

    const {calculatedEarnings, commissionPercentage, calculateEarnings} = useCommissionCalculation(
        formData,
        excludedAmenityPriceSum
    )

    // Computed property to check if user has interacted with amenity checkboxes
    const hasUserInteractedWithAmenities = computed(() => {
        if (formData.value.sustainabilityLevel !== 'SUSTAINABLE' && formData.value.sustainabilityLevel !== 'ZERO') {
            return true // Not applicable for BASIC rooms
        }

        // For existing rooms (with ID), allow saving immediately since they already have amenities configured
        if (formData.value?.id) {
            return true
        }

        // For new SUSTAINABLE rooms, check if any amenity checkbox has been checked
        if (formData.value.sustainabilityLevel === 'SUSTAINABLE') {
            const sustainableAmenities = formData.value.sustainableAmenitiesEnabled || {}
            return Object.values(sustainableAmenities).some(checked => checked === true)
        }

        // For new ZERO rooms, check if any amenity checkbox has been checked (both basic and extra)
        if (formData.value.sustainabilityLevel === 'ZERO') {
            const zeroBasicAmenities = formData.value.zeroAmenitiesEnabled || {}
            const zeroExtraAmenities = formData.value.zeroAmenitiesExtraEnabled || {}

            const hasBasicInteraction = Object.values(zeroBasicAmenities).some(checked => checked === true)
            const hasExtraInteraction = Object.values(zeroExtraAmenities).some(checked => checked === true)

            return hasBasicInteraction || hasExtraInteraction
        }

        return true
    })

    // Computed property to check if amenity selection meets room type requirements
    const hasValidAmenitySelection = computed(() => {
        if (formData.value.sustainabilityLevel !== 'SUSTAINABLE' && formData.value.sustainabilityLevel !== 'ZERO') {
            return true // Not applicable for BASIC rooms
        }

        // Only validate if amenities data is loaded
        if (!sustainableAmenitiesData.value || sustainableAmenitiesData.value.length === 0) {
            return true // Wait for data to load
        }

        // For SUSTAINABLE rooms, must have at least 2 eco amenities checked
        if (formData.value.sustainabilityLevel === 'SUSTAINABLE') {
            const ecoAmenities = sustainableAmenitiesData.value.filter(
                amenity => amenity.category === 'ECO_AMENITIES' && amenity.basicEnabled === true
            )
            const checkedEcoAmenities = ecoAmenities.filter(amenity => {
                const amenityKey = amenity.amenityTypeId || amenity.id
                return formData.value.sustainableAmenitiesEnabled?.[amenityKey]
            })

            return checkedEcoAmenities.length >= 2
        }

        // For ZERO rooms, must have at least 1 eco amenity AND minimum 2 total amenities checked
        if (formData.value.sustainabilityLevel === 'ZERO') {
            const ecoAmenities = sustainableAmenitiesData.value.filter(
                amenity => amenity.category === 'ECO_AMENITIES' && amenity.basicEnabled === true
            )
            const allAmenities = sustainableAmenitiesData.value.filter(amenity => amenity.basicEnabled === true)

            const checkedEcoAmenities = ecoAmenities.filter(amenity => {
                const amenityKey = amenity.amenityTypeId || amenity.id
                return formData.value.zeroAmenitiesEnabled?.[amenityKey]
            })
            const checkedAnyAmenities = allAmenities.filter(amenity => {
                const amenityKey = amenity.amenityTypeId || amenity.id
                return formData.value.zeroAmenitiesEnabled?.[amenityKey]
            })

            return checkedEcoAmenities.length >= 1 && checkedAnyAmenities.length >= 2
        }

        return true
    })

    // Computed property to check if there are any amenity validation errors
    const hasAmenityValidationErrors = computed(() => {
        if (formData.value.sustainabilityLevel !== 'SUSTAINABLE' && formData.value.sustainabilityLevel !== 'ZERO') {
            return false
        }

        // Check sustainable amenities (only basicEnabled: true amenities)
        if (formData.value.sustainabilityLevel === 'SUSTAINABLE') {
            const enabledAmenities = Object.keys(formData.value.sustainableAmenitiesEnabled || {}).filter(
                key => formData.value.sustainableAmenitiesEnabled[key]
            )

            for (const amenityId of enabledAmenities) {
                const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) == amenityId)
                if (!amenity) continue

                // Only validate amenities with basicEnabled: true for sustainable rooms
                if (amenity.basicEnabled !== true) continue

                const price = getAmenityValue(amenityId, 'price')
                if (!price || parseFloat(price) <= 0) {
                    return true
                }

                // Skip energy-related validation for OTHER category amenities
                if (amenity.category === 'OTHER') {
                    continue
                }

                if (amenity.kwconsumption !== null) {
                    const kwValue = getAmenityValue(amenityId, 'kwconsumption')
                    if (
                        kwValue === null ||
                        kwValue === undefined ||
                        kwValue === '' ||
                        !validateAmenityInput(amenityId, 'kwconsumption', kwValue)
                    ) {
                        return true
                    }
                }

                if (amenity.usageHours !== null) {
                    const usageValue = getAmenityValue(amenityId, 'usageHours')
                    if (
                        usageValue === null ||
                        usageValue === undefined ||
                        usageValue === '' ||
                        !validateAmenityInput(amenityId, 'usageHours', usageValue)
                    ) {
                        return true
                    }
                }
            }
        }

        // Check zero amenities (validate both sections separately)
        if (formData.value.sustainabilityLevel === 'ZERO') {
            // First section: basicEnabled: true amenities
            const enabledBasicAmenities = Object.keys(formData.value.zeroAmenitiesEnabled || {}).filter(
                key => formData.value.zeroAmenitiesEnabled[key]
            )

            for (const amenityId of enabledBasicAmenities) {
                const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) == amenityId)
                if (!amenity) continue

                // Only validate amenities with basicEnabled: true for first section
                if (amenity.basicEnabled !== true) continue

                const price = getAmenityValue(amenityId, 'price')
                if (!price || parseFloat(price) <= 0) {
                    return true
                }

                // Skip energy-related validation for OTHER category amenities
                if (amenity.category === 'OTHER') {
                    continue
                }

                if (amenity.kwconsumption !== null) {
                    const kwValue = getAmenityValue(amenityId, 'kwconsumption')
                    if (
                        kwValue === null ||
                        kwValue === undefined ||
                        kwValue === '' ||
                        !validateAmenityInput(amenityId, 'kwconsumption', kwValue)
                    ) {
                        return true
                    }
                }

                if (amenity.usageHours !== null) {
                    const usageValue = getAmenityValue(amenityId, 'usageHours')
                    if (
                        usageValue === null ||
                        usageValue === undefined ||
                        usageValue === '' ||
                        !validateAmenityInput(amenityId, 'usageHours', usageValue)
                    ) {
                        return true
                    }
                }
            }

            // Second section: basicEnabled: false amenities
            const enabledExtraAmenities = Object.keys(formData.value.zeroAmenitiesExtraEnabled || {}).filter(
                key => formData.value.zeroAmenitiesExtraEnabled[key]
            )

            for (const amenityId of enabledExtraAmenities) {
                const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) == amenityId)
                if (!amenity) continue

                // Only validate amenities with basicEnabled: false for second section
                if (amenity.basicEnabled !== false) continue

                const price = getAmenityValue(amenityId, 'price')
                if (!price || parseFloat(price) <= 0) {
                    return true
                }

                // Skip energy-related validation for OTHER category amenities
                if (amenity.category === 'OTHER') {
                    continue
                }

                if (amenity.kwconsumption !== null) {
                    const kwValue = getAmenityValue(amenityId, 'kwconsumption')
                    if (
                        kwValue === null ||
                        kwValue === undefined ||
                        kwValue === '' ||
                        !validateAmenityInput(amenityId, 'kwconsumption', kwValue)
                    ) {
                        return true
                    }
                }

                if (amenity.usageHours !== null) {
                    const usageValue = getAmenityValue(amenityId, 'usageHours')
                    if (
                        usageValue === null ||
                        usageValue === undefined ||
                        usageValue === '' ||
                        !validateAmenityInput(amenityId, 'usageHours', usageValue)
                    ) {
                        return true
                    }
                }
            }
        }

        return false
    })

    // Emit events
    const emit = defineEmits<{
        (e: 'save'): void
        // eslint-disable-next-line @typescript-eslint/unified-signatures
        (e: 'cancel'): void
    }>()

    // Watch for changes to the room type
    watch(
        () => formData.value?.roomType,
        () => {
            // Room type change handling if needed
        }
    )

    // Watch for changes to sustainable amenities checkboxes
    watch(
        () => formData.value?.sustainableAmenitiesEnabled,
        (newValue, oldValue) => {
            if (newValue && oldValue) {
                Object.keys(newValue).forEach(amenityId => {
                    // If checkbox was just checked (false -> true)
                    if (newValue[amenityId] && !oldValue[amenityId]) {
                        initializeAmenityEnergyData(amenityId)
                    }
                })
            }
        },
        {deep: true}
    )

    // Watch for changes to zero amenities checkboxes (basicEnabled = true)
    watch(
        () => formData.value?.zeroAmenitiesEnabled,
        (newValue, oldValue) => {
            if (newValue && oldValue) {
                Object.keys(newValue).forEach(amenityId => {
                    // If checkbox was just checked (false -> true)
                    if (newValue[amenityId] && !oldValue[amenityId]) {
                        initializeAmenityEnergyData(amenityId)
                    }
                })
            }
        },
        {deep: true}
    )

    // Watch for changes to zero amenities extra checkboxes (basicEnabled = false)
    watch(
        () => formData.value?.zeroAmenitiesExtraEnabled,
        (newValue, oldValue) => {
            if (newValue && oldValue) {
                Object.keys(newValue).forEach(amenityId => {
                    // If checkbox was just checked (false -> true)
                    if (newValue[amenityId] && !oldValue[amenityId]) {
                        initializeAmenityEnergyData(amenityId)
                    }
                })
            }
        },
        {deep: true}
    )

    // Watch for changes to room data that affect earnings calculation
    watch(
        () => [formData.value?.id, formData.value?.sustainabilityLevel],
        () => {
            calculateEarnings()
        },
        {deep: true}
    )

    // Watch for changes to allocation options to reset validation errors when data becomes valid
    watch(
        () => formData.value?.allocationOptions,
        () => {
            // Only reset validation error if it's currently showing
            if (isShowVallidationError.value) {
                const data = formData.value
                if (data?.allocationOptions && data?.maxTotalOccupancy) {
                    // Check if all allocation options are now valid
                    const hasInvalidAllocation = data.allocationOptions.find(
                        ({adults, children}) => adults + children > data.maxTotalOccupancy || adults + children === 0
                    )

                    // If no invalid allocations found, reset the validation error
                    if (!hasInvalidAllocation) {
                        isShowVallidationError.value = false
                    }
                }
            }
        },
        {deep: true}
    )

    // Watch for changes to maxTotalOccupancy to reset validation errors when capacity changes
    watch(
        () => formData.value?.maxTotalOccupancy,
        () => {
            // Only reset validation error if it's currently showing
            if (isShowVallidationError.value) {
                const data = formData.value
                if (data?.allocationOptions && data?.maxTotalOccupancy) {
                    // Check if all allocation options are now valid with the new capacity
                    const hasInvalidAllocation = data.allocationOptions.find(
                        ({adults, children}) => adults + children > data.maxTotalOccupancy || adults + children === 0
                    )

                    // If no invalid allocations found, reset the validation error
                    if (!hasInvalidAllocation) {
                        isShowVallidationError.value = false
                    }
                }
            }
        }
    )

    // Function to load existing amenities for BASIC room types when editing
    const loadBasicRoomAmenities = async () => {
        if (!formData.value?.id) {
            return
        }

        try {
            const response = await app.$api.hotelRoomAmenities.get({
                productId: supplierStore.fullContract.id,
                roomId: formData.value.id,
                locale: 'all',
            })

            // Update checkbox states based on existing amenities
            if (response && response.length > 0) {
                response.forEach(amenity => {
                    if (amenity.amenityTypeId && amenity.enabled !== undefined) {
                        formData.value.roomAmenityEnabled[amenity.amenityTypeId] = amenity.enabled
                    }
                })
            }
        } catch (error) {
            console.error('Error loading existing amenities for BASIC room:', error)
        }
    }

    // Function to load sustainable room amenities
    const loadSustainableAmenities = async () => {
        // For existing sustainable/zero rooms, load from the room itself
        // For new sustainable/zero rooms, load from the basic room
        let roomIdToLoad
        if (formData.value?.id) {
            roomIdToLoad = formData.value.id
        } else if (formData.value?.basicRoomId) {
            roomIdToLoad = formData.value.basicRoomId
        } else {
            return
        }

        try {
            sustainableAmenitiesLoading.value = true

            const response = await app.$api.hotelRoomAmenities.get({
                productId: supplierStore.fullContract.id,
                roomId: roomIdToLoad,
                locale: 'all',
            })

            sustainableAmenitiesData.value = response || []

            // Initialize checkbox states (all unchecked initially for new rooms)
            if (!formData.value.sustainableAmenitiesEnabled) {
                formData.value.sustainableAmenitiesEnabled = {}
            }
            if (!formData.value.zeroAmenitiesEnabled) {
                formData.value.zeroAmenitiesEnabled = {}
            }
            if (!formData.value.zeroAmenitiesExtraEnabled) {
                formData.value.zeroAmenitiesExtraEnabled = {}
            }

            response.forEach(amenity => {
                // Use amenityTypeId for consistency with saving logic
                const amenityKey = amenity.amenityTypeId || amenity.id

                // Initialize checkbox states based on the loaded amenities
                if (formData.value.sustainableAmenitiesEnabled[amenityKey] === undefined) {
                    // For new sustainable rooms, start with all checkboxes unchecked
                    // For existing sustainable rooms, invert the saved enabled state (enabled: false = checked: true)
                    if (formData.value?.id) {
                        // Existing room - invert saved state (enabled: false means amenity is removed, so checkbox should be checked)
                        formData.value.sustainableAmenitiesEnabled[amenityKey] = !(amenity.enabled || false)
                    } else {
                        // New room - start unchecked
                        formData.value.sustainableAmenitiesEnabled[amenityKey] = false
                    }
                }
                if (formData.value.zeroAmenitiesEnabled[amenityKey] === undefined) {
                    // For new zero rooms, start with all checkboxes unchecked
                    // For existing zero rooms, invert the saved enabled state for basicEnabled=true amenities
                    if (formData.value?.id && amenity.basicEnabled === true) {
                        // Existing room - invert saved state (enabled: false means amenity is removed, so checkbox should be checked)
                        formData.value.zeroAmenitiesEnabled[amenityKey] = !(amenity.enabled || false)
                    } else {
                        // New room - start unchecked
                        formData.value.zeroAmenitiesEnabled[amenityKey] = false
                    }
                }
                if (formData.value.zeroAmenitiesExtraEnabled[amenityKey] === undefined) {
                    if (formData.value?.id) {
                        // Existing room - use saved state
                        if (amenity.basicEnabled === true) {
                            // For first section (basicEnabled = true): extraEnabled should be opposite of enabled
                            const enabled = amenity.enabled || false
                            formData.value.zeroAmenitiesExtraEnabled[amenityKey] = !enabled
                        } else {
                            // For second section (basicEnabled = false): use saved extraEnabled state
                            formData.value.zeroAmenitiesExtraEnabled[amenityKey] = amenity.extraEnabled || false
                        }
                    } else {
                        // New room - start unchecked
                        formData.value.zeroAmenitiesExtraEnabled[amenityKey] = false
                    }
                }

                // For existing rooms, populate amenityModifications with saved values
                if (formData.value?.id) {
                    // Initialize amenityModifications for this amenity if it doesn't exist
                    if (!amenityModifications.value[amenityKey]) {
                        amenityModifications.value[amenityKey] = {}
                    }

                    // Store the saved values in amenityModifications
                    if (amenity.price !== undefined && amenity.price !== null) {
                        amenityModifications.value[amenityKey]['price'] = amenity.price
                    }
                    if (amenity.energyType !== undefined && amenity.energyType !== null) {
                        amenityModifications.value[amenityKey]['energyType'] = amenity.energyType
                    }
                    if (amenity.kwconsumption !== undefined && amenity.kwconsumption !== null) {
                        amenityModifications.value[amenityKey]['kwconsumption'] = amenity.kwconsumption
                    }
                    if (amenity.usageHours !== undefined && amenity.usageHours !== null) {
                        amenityModifications.value[amenityKey]['usageHours'] = amenity.usageHours
                    }
                }

                // Initialize default values from API for Usage and Watts fields during form initialization
                initializeAmenityDefaults(amenityKey)

                // Don't pre-set energy type - only set it when user checks the amenity
            })
        } catch (error) {
            console.error('Error loading sustainable amenities:', error)
            sustainableAmenitiesData.value = []
        } finally {
            sustainableAmenitiesLoading.value = false
        }
    }

    onMounted(async () => {
        // Load sustainable amenities if this is a sustainable or zero room
        if (formData.value?.sustainabilityLevel === 'SUSTAINABLE' || formData.value?.sustainabilityLevel === 'ZERO') {
            await loadSustainableAmenities()

            // If this is a new room type with existing amenity prices from another variant, pre-populate them
            if (
                !formData.value?.id &&
                formData.value?.existingAmenityPrices &&
                formData.value.existingAmenityPrices.length > 0
            ) {
                // Pre-populate amenity modifications with prices from existing variant
                formData.value.existingAmenityPrices.forEach(existingAmenity => {
                    const amenityKey = existingAmenity.amenityTypeId || existingAmenity.id

                    // Initialize amenityModifications for this amenity if it doesn't exist
                    if (!amenityModifications.value[amenityKey]) {
                        amenityModifications.value[amenityKey] = {}
                    }

                    // Copy price and other relevant data from existing variant
                    if (existingAmenity.price !== null && existingAmenity.price !== undefined) {
                        amenityModifications.value[amenityKey].price = existingAmenity.price
                    }
                    if (existingAmenity.usageHours !== null && existingAmenity.usageHours !== undefined) {
                        amenityModifications.value[amenityKey].usageHours = existingAmenity.usageHours
                    }
                    if (existingAmenity.kwconsumption !== null && existingAmenity.kwconsumption !== undefined) {
                        amenityModifications.value[amenityKey].kwconsumption = existingAmenity.kwconsumption
                    }
                    if (existingAmenity.energyType) {
                        amenityModifications.value[amenityKey].energyType = existingAmenity.energyType
                    }
                })

                // Clean up the temporary field
                delete formData.value.existingAmenityPrices
            }
        }

        // Fetch amenities from the new API endpoint
        try {
            const amenitiesResponse = await app.$api.hotelRoomAmenitiesTypes.get({locale: 'all'})

            // Store amenities data
            amenitiesData.value = amenitiesResponse || []
        } catch (error) {
            console.error('Error fetching hotel room amenities types:', error)
        }

        // Load existing amenities for BASIC room types when editing
        if (formData.value?.id && formData.value?.sustainabilityLevel === 'BASIC') {
            await loadBasicRoomAmenities()
        }

        // Set default room type to STANDARD for new room types
        if (!formData.value?.id && !formData.value?.roomType) {
            formData.value.roomType = 'STANDARD'
        }

        if (formData.value.beds?.length) {
            formData.value.beds = formData.value.beds.map(item => ({...item, id: nanoid()}))
        }
        if (formData.value.allocationOptions?.length) {
            formData.value.allocationOptions = formData.value.allocationOptions.map(item => ({...item, id: nanoid()}))
        }

        // Initialize roomAmenityEnabled if it doesn't exist or is an array
        if (!formData.value.roomAmenityEnabled || Array.isArray(formData.value.roomAmenityEnabled)) {
            // If it's an array, try to convert it to an object
            if (Array.isArray(formData.value.roomAmenityEnabled)) {
                const roomAmenityEnabledArray = formData.value.roomAmenityEnabled
                formData.value.roomAmenityEnabled = {}

                // If the array has values, try to convert them
                if (roomAmenityEnabledArray.length > 0) {
                    const allServices = clone(supplierStore.hotelServiceDictionaryInfo)['IN_ROOM'] || []
                    allServices.forEach(service => {
                        if (service.id) {
                            formData.value.roomAmenityEnabled[service.id] = roomAmenityEnabledArray.includes(service.id)
                        }
                    })
                }
            } else {
                formData.value.roomAmenityEnabled = {}
            }
        }

        // Initialize amenities from the new API data
        if (amenitiesData.value.length > 0) {
            amenitiesData.value.forEach(amenity => {
                if (amenity.id && formData.value.roomAmenityEnabled[amenity.id] === undefined) {
                    // Set default value to false for new amenities
                    formData.value.roomAmenityEnabled[amenity.id] = false
                }
            })
        }

        // Initialize sustainableAmenitiesEnabled for sustainable rooms
        if (!formData.value.sustainableAmenitiesEnabled) {
            formData.value.sustainableAmenitiesEnabled = {}
        }

        // Initialize zeroAmenitiesEnabled for zero rooms (basicEnabled = true)
        if (!formData.value.zeroAmenitiesEnabled) {
            formData.value.zeroAmenitiesEnabled = {}
        }

        // Initialize zeroAmenitiesExtraEnabled for zero rooms (basicEnabled = false)
        if (!formData.value.zeroAmenitiesExtraEnabled) {
            formData.value.zeroAmenitiesExtraEnabled = {}
        }

        // Get all services including hidden ones
        const allServices = clone(supplierStore.hotelServiceDictionaryInfo)['IN_ROOM'] || []

        // For new room types (no id), set visible amenities to checked by default
        if (!formData.value?.id) {
            // Set all visible amenities to checked by default for new room types
            useRoomServices.value.forEach(({id}) => {
                if (id) formData.value.roomAmenityEnabled[id] = true
            })

            // Set default room type to 'STANDARD'
            formData.value.roomType = 'STANDARD'
        }

        // Always ensure hidden amenities are unchecked by default
        allServices.forEach(service => {
            if (service.id && hiddenAmenities.includes(service.name)) {
                formData.value.roomAmenityEnabled[service.id] = false
            }
        })

        // Calculate earnings for commission block
        await calculateEarnings()
    })

    // Computed properties for warning messages
    const showBasicRoomWarnings = computed(() => {
        return amenitiesData.value.length > 0
    })

    const hasEnoughEcoAmenitiesForSustainable = computed(() => {
        if (!amenitiesData.value.length) return true

        const ecoAmenities = amenitiesData.value.filter(amenity => amenity.category === 'ECO_AMENITIES')
        const checkedEcoAmenities = ecoAmenities.filter(amenity => formData.value.roomAmenityEnabled[amenity.id])

        return checkedEcoAmenities.length >= 2
    })

    const hasEnoughAmenitiesForZero = computed(() => {
        if (!amenitiesData.value.length) return true

        const ecoAmenities = amenitiesData.value.filter(amenity => amenity.category === 'ECO_AMENITIES')
        const allAmenities = amenitiesData.value

        const checkedEcoAmenities = ecoAmenities.filter(amenity => formData.value.roomAmenityEnabled[amenity.id])
        const checkedAnyAmenities = allAmenities.filter(amenity => formData.value.roomAmenityEnabled[amenity.id])

        return checkedEcoAmenities.length >= 1 && checkedAnyAmenities.length >= 2
    })

    const validateForm = () => {
        const data = clone(formData.value)
        isShowVallidationError.value = false

        // Total occupancy is derived (read-only in the form): base occupancy
        // + extra persons on extra bed + extra persons without a place.
        data.minTotalOccupancy = data.minOccupancy
        data.maxTotalOccupancy =
            (Number(data.maxOccupancy) || 0) + (Number(data.maxExtraPlace) || 0) + (Number(data.maxWithoutPlace) || 0)

        // Basic validation for allocation options
        const wrongAllocation = data.allocationOptions.find(
            ({adults, children}) => adults + children > data.maxTotalOccupancy || adults + children === 0
        )
        if (wrongAllocation) {
            isShowVallidationError.value = true
            return
        }

        // Validation for SUSTAINABLE room types
        if (data.sustainabilityLevel === 'SUSTAINABLE') {
            const ecoAmenities = sustainableAmenitiesData.value.filter(
                amenity => amenity.category === 'ECO_AMENITIES' && amenity.basicEnabled === true
            )
            const checkedEcoAmenities = ecoAmenities.filter(amenity => {
                const amenityKey = amenity.amenityTypeId || amenity.id
                return formData.value.sustainableAmenitiesEnabled?.[amenityKey]
            })

            if (checkedEcoAmenities.length < 2) {
                app.$toast.error(t('error_message.sustainable_room_validation'))
                isShowVallidationError.value = true
                return
            }
        }

        // Validation for ZERO room types
        if (data.sustainabilityLevel === 'ZERO') {
            const ecoAmenities = sustainableAmenitiesData.value.filter(
                amenity => amenity.category === 'ECO_AMENITIES' && amenity.basicEnabled === true
            )
            const allAmenities = sustainableAmenitiesData.value.filter(amenity => amenity.basicEnabled === true)

            const checkedEcoAmenities = ecoAmenities.filter(amenity => {
                const amenityKey = amenity.amenityTypeId || amenity.id
                return formData.value.zeroAmenitiesEnabled?.[amenityKey]
            })
            const checkedAnyAmenities = allAmenities.filter(amenity => {
                const amenityKey = amenity.amenityTypeId || amenity.id
                return formData.value.zeroAmenitiesEnabled?.[amenityKey]
            })

            if (checkedEcoAmenities.length < 1 || checkedAnyAmenities.length < 2) {
                app.$toast.error(t('error_message.zero_room_validation'))
                isShowVallidationError.value = true
                return
            }
        }

        // Client-side validation for amenity required fields
        if (data.sustainabilityLevel === 'SUSTAINABLE' || data.sustainabilityLevel === 'ZERO') {
            let hasAmenityValidationErrors = false

            // Check sustainable amenities (only basicEnabled: true amenities)
            if (data.sustainabilityLevel === 'SUSTAINABLE') {
                const enabledAmenities = Object.keys(data.sustainableAmenitiesEnabled || {}).filter(
                    key => data.sustainableAmenitiesEnabled[key]
                )

                for (const amenityId of enabledAmenities) {
                    const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) == amenityId)
                    if (!amenity) continue

                    // Only validate amenities with basicEnabled: true for sustainable rooms
                    if (amenity.basicEnabled !== true) continue

                    const price = getAmenityValue(amenityId, 'price')
                    if (!price || parseFloat(price) <= 0) {
                        hasAmenityValidationErrors = true
                        break
                    }

                    // Skip energy-related validation for OTHER category amenities
                    if (amenity.category === 'OTHER') {
                        continue
                    }

                    if (amenity.kwconsumption !== null) {
                        const kwValue = getAmenityValue(amenityId, 'kwconsumption')
                        if (
                            kwValue === null ||
                            kwValue === undefined ||
                            kwValue === '' ||
                            !validateAmenityInput(amenityId, 'kwconsumption', kwValue)
                        ) {
                            hasAmenityValidationErrors = true
                            break
                        }
                    }

                    if (amenity.usageHours !== null) {
                        const usageValue = getAmenityValue(amenityId, 'usageHours')
                        if (
                            usageValue === null ||
                            usageValue === undefined ||
                            usageValue === '' ||
                            !validateAmenityInput(amenityId, 'usageHours', usageValue)
                        ) {
                            hasAmenityValidationErrors = true
                            break
                        }
                    }
                }
            }

            // Check zero amenities (validate both sections separately)
            if (data.sustainabilityLevel === 'ZERO') {
                // First section: basicEnabled: true amenities
                const enabledBasicAmenities = Object.keys(data.zeroAmenitiesEnabled || {}).filter(
                    key => data.zeroAmenitiesEnabled[key]
                )

                for (const amenityId of enabledBasicAmenities) {
                    const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) == amenityId)
                    if (!amenity) continue

                    // Only validate amenities with basicEnabled: true for first section
                    if (amenity.basicEnabled !== true) continue

                    const price = getAmenityValue(amenityId, 'price')
                    if (!price || parseFloat(price) <= 0) {
                        hasAmenityValidationErrors = true
                        break
                    }

                    // Skip energy-related validation for OTHER category amenities
                    if (amenity.category === 'OTHER') {
                        continue
                    }

                    if (amenity.kwconsumption !== null) {
                        const kwValue = getAmenityValue(amenityId, 'kwconsumption')
                        if (
                            kwValue === null ||
                            kwValue === undefined ||
                            kwValue === '' ||
                            !validateAmenityInput(amenityId, 'kwconsumption', kwValue)
                        ) {
                            hasAmenityValidationErrors = true
                            break
                        }
                    }

                    if (amenity.usageHours !== null) {
                        const usageValue = getAmenityValue(amenityId, 'usageHours')
                        if (
                            usageValue === null ||
                            usageValue === undefined ||
                            usageValue === '' ||
                            !validateAmenityInput(amenityId, 'usageHours', usageValue)
                        ) {
                            hasAmenityValidationErrors = true
                            break
                        }
                    }
                }

                // Second section: basicEnabled: false amenities
                const enabledExtraAmenities = Object.keys(data.zeroAmenitiesExtraEnabled || {}).filter(
                    key => data.zeroAmenitiesExtraEnabled[key]
                )

                for (const amenityId of enabledExtraAmenities) {
                    const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) == amenityId)
                    if (!amenity) continue

                    // Only validate amenities with basicEnabled: false for second section
                    if (amenity.basicEnabled !== false) continue

                    const price = getAmenityValue(amenityId, 'price')
                    if (!price || parseFloat(price) <= 0) {
                        hasAmenityValidationErrors = true
                        break
                    }

                    // Skip energy-related validation for OTHER category amenities
                    if (amenity.category === 'OTHER') {
                        continue
                    }

                    if (amenity.kwconsumption !== null) {
                        const kwValue = getAmenityValue(amenityId, 'kwconsumption')
                        if (
                            kwValue === null ||
                            kwValue === undefined ||
                            kwValue === '' ||
                            !validateAmenityInput(amenityId, 'kwconsumption', kwValue)
                        ) {
                            hasAmenityValidationErrors = true
                            break
                        }
                    }

                    if (amenity.usageHours !== null) {
                        const usageValue = getAmenityValue(amenityId, 'usageHours')
                        if (
                            usageValue === null ||
                            usageValue === undefined ||
                            usageValue === '' ||
                            !validateAmenityInput(amenityId, 'usageHours', usageValue)
                        ) {
                            hasAmenityValidationErrors = true
                            break
                        }
                    }
                }
            }

            if (hasAmenityValidationErrors) {
                app.$toast.error(t('room_types.fill_required_amenity_fields'))
                isShowVallidationError.value = true
                return
            }
        }

        // Note: Additional kW consumption validation is handled by the server
        // Each amenity type has its own specific constraints that will be validated server-side

        if (!isShowVallidationError.value) {
            // Clean up IDs
            data.beds.forEach(obj => {
                delete obj.id
            })
            data.allocationOptions.forEach(obj => {
                delete obj.id
            })

            // Ensure roomAmenityEnabled is an object (Record<string, boolean>), not an array
            if (Array.isArray(data.roomAmenityEnabled)) {
                console.warn('roomAmenityEnabled was an array, converting to object')
                const roomAmenityEnabledObj = {}
                // Convert any existing values if possible
                if (data.roomAmenityEnabled.length > 0) {
                    useRoomServices.value.forEach(({id}) => {
                        if (id) roomAmenityEnabledObj[id] = data.roomAmenityEnabled.includes(id)
                    })
                }
                data.roomAmenityEnabled = roomAmenityEnabledObj
            }

            // Remove any special room type related fields and UI-only fields
            delete data.ecoAmenitiesEnabled
            delete data.energySavingSolutionsEnabled
            delete data.specialExtraServices
            delete data.isNewVariant
            delete data.standardRoomId
            delete data.sustainableAmenitiesEnabled // UI-only field for sustainable room amenities checkboxes
            delete data.zeroAmenitiesEnabled // UI-only field for zero room amenities checkboxes (basicEnabled=true)
            delete data.zeroAmenitiesExtraEnabled // UI-only field for zero room amenities checkboxes (basicEnabled=false)
            delete data.existingAmenityPrices // UI-only field for pre-populating prices from existing variants

            // Save the room type
            save(data)
        }
    }

    const save = async (data): Promise<void> => {
        try {
            isLoading.value = true
            // Clear previous validation errors
            amenityValidationErrors.value = []

            // Make sure we're not sending any fields that the API doesn't recognize
            // These fields are used only in the UI and should not be sent to the API
            delete data.ecoAmenitiesEnabled
            delete data.energySavingSolutionsEnabled
            delete data.specialExtraServices
            delete data.roomType // Remove roomType field as API doesn't recognize it
            delete data.productId // Remove productId field as API doesn't recognize it
            delete data.roomAmenityEnabled // Remove roomAmenityEnabled - handled separately via amenities API
            delete data.sustainableAmenitiesEnabled // UI-only field for sustainable room amenities checkboxes
            delete data.zeroAmenitiesEnabled // UI-only field for zero room amenities checkboxes (basicEnabled=true)
            delete data.zeroAmenitiesExtraEnabled // UI-only field for zero room amenities checkboxes (basicEnabled=false)
            delete data.existingAmenityPrices // UI-only field for pre-populating prices from existing variants
            // CORE-32250: the Channel Managers block is loaded and rendered for BASIC rooms
            // only, so for SUSTAINABLE/ZERO rooms — and for a BASIC room whose GET is still in
            // flight or has failed — channelManagersConfigItems is empty. The backend applies
            // channelManagerObjects as a full replacement (clear + addAll), so sending that
            // empty list would silently drop the room's real Channel Manager bindings.
            // Send the list only when it was actually loaded; otherwise leave the field as the
            // room type GET returned it, which round-trips the existing bindings unchanged.
            if (isChannelManagersConfigLoaded.value) {
                data.channelManagerObjects = clone(channelManagersConfigItems.value)
            } else if (!data.id) {
                // A new SUSTAINABLE/ZERO room is created by copying the BASIC room object, so it
                // inherits its channelManagerObjects. Those external ids belong to the BASIC room
                // and must not be duplicated onto the new one.
                data.channelManagerObjects = []
            }

            let roomTypeResponse
            if (data?.id) {
                roomTypeResponse = await app.$api.hotelDescriptionRoomType.put(
                    supplierStore.hotelInfo.hotelId,
                    data?.id,
                    data
                )
            } else {
                roomTypeResponse = await app.$api.hotelDescriptionRoomType.post(supplierStore.hotelInfo.hotelId, data)
            }

            // After successful room type creation/update, save amenities if we have amenities data
            if (roomTypeResponse?.id) {
                if (
                    formData.value?.sustainabilityLevel === 'SUSTAINABLE' &&
                    sustainableAmenitiesData.value.length > 0
                ) {
                    await saveSustainableAmenities(roomTypeResponse.id)
                } else if (
                    formData.value?.sustainabilityLevel === 'ZERO' &&
                    sustainableAmenitiesData.value.length > 0
                ) {
                    await saveZeroAmenities(roomTypeResponse.id)
                } else if (amenitiesData.value.length > 0) {
                    await saveAmenities(roomTypeResponse.id)
                }
            }

            if (!isHotelDescriptionMode) await supplierStore.loadRoomTypeData()

            // Show appropriate success message based on room type
            if (formData.value?.sustainabilityLevel === 'BASIC' && amenitiesData.value.length > 0) {
                app.$toast.success(
                    t(
                        'success_message.basic_room_saved_with_amenities',
                        'Room type saved successfully! Remember to update amenities in special rooms (SUSTAINABLE/ZERO) if needed.'
                    )
                )
            } else {
                app.$toast.success(t('success_message.saved_successfully'))
            }

            emit('save')
        } catch (e: any) {
            console.error('Error saving room type:', e, e.response?.data)

            // Check if this is an amenity validation error
            if (amenityValidationErrors.value.length > 0) {
                // Don't emit save event, keep user on form to fix validation errors
                return
            }

            // Show more detailed error message if available
            if (e.response?.data?.errors && e.response.data.errors.length > 0) {
                const errorMessage = e.response.data.errors[0].message || t('error_message.save_error')
                app.$toast.error(errorMessage)
                console.error('API error details:', e.response.data.errors)
            } else {
                app.$toast.error(t('error_message.save_error'))
            }
        } finally {
            isLoading.value = false
        }
    }

    const saveAmenities = async (roomId: string | number): Promise<void> => {
        try {
            // Transform amenities data based on user selections
            const amenitiesPayload = amenitiesData.value.map(amenity => {
                const isEnabled = formData.value.roomAmenityEnabled[amenity.id] || false

                return {
                    amenityTypeId: amenity.id, // For BASIC rooms, amenitiesData uses 'id' field
                    category: amenity.category,
                    enabled: isEnabled,
                    basicEnabled: isEnabled, // Copy of enabled value for BASIC room type
                    extraEnabled: false, // Always false for BASIC room type
                    usageHours: amenity.usageHours || null,
                    kwconsumption: convertToNumber(amenity.kwconsumption),
                    energyType: amenity.energyType || null,
                    price: convertToNumber(amenity.price),
                    name: amenity.name,
                }
            })

            await app.$api.hotelRoomAmenitiesTypes.post(
                {
                    productId: supplierStore.fullContract.id,
                    hotelId: supplierStore.hotelInfo.hotelId,
                    roomId: roomId,
                },
                amenitiesPayload
            )
        } catch (error) {
            console.error('Error saving amenities:', error)
            app.$toast.error(t('room_types.error_saving_amenities'))
            // Throw error to prevent the main save flow from continuing
            throw error
        }
    }

    const saveSustainableAmenities = async (roomId: string | number): Promise<void> => {
        try {
            // Transform sustainable amenities data based on user selections
            const amenitiesPayload = sustainableAmenitiesData.value.map(amenity => {
                const amenityKey = amenity.amenityTypeId || amenity.id
                const isChecked = formData.value.sustainableAmenitiesEnabled?.[amenityKey] || false
                // For sustainable rooms: checked = disabled (false), unchecked = enabled (true)
                const isEnabled = !isChecked

                // Ensure all amenities with energy-related fields have proper energy type and kW consumption
                let energyType = getAmenityValue(amenityKey, 'energyType') || amenity.energyType
                let kwconsumption = getAmenityValue(amenityKey, 'kwconsumption') || amenity.kwconsumption

                // If amenity has energy-related fields but no energy type, set defaults
                if (amenity.kwconsumption !== null && !energyType) {
                    energyType = 'ELECTRICAL'
                    kwconsumption = getSafeKwConsumption(amenity.amenityTypeId || amenity.id)
                }

                return {
                    amenityTypeId: amenity.amenityTypeId,
                    category: amenity.category,
                    enabled: isEnabled,
                    // Set basicEnabled based on whether the amenity was enabled in the basic room
                    // This should reflect the original enabled state from the basic room, not the current enabled state
                    basicEnabled: amenity.basicEnabled || false,
                    extraEnabled: false, // Always false for SUSTAINABLE room type
                    usageHours: convertToNumber(getAmenityValue(amenityKey, 'usageHours')),
                    kwconsumption: convertToNumber(kwconsumption),
                    energyType: energyType || null,
                    price: convertToNumber(getAmenityValue(amenityKey, 'price') || amenity.price),
                    name: amenity.name,
                }
            })

            await app.$api.hotelRoomAmenities.post(
                {
                    productId: supplierStore.fullContract.id,
                    hotelId: supplierStore.hotelInfo.hotelId,
                    roomId: roomId,
                },
                amenitiesPayload
            )
        } catch (error: any) {
            console.error('Error saving sustainable amenities:', error)

            // Handle validation errors from server
            if (error.errors && Array.isArray(error.errors)) {
                amenityValidationErrors.value = error.errors.filter((e: any) => e.errorType === 'validation')
                if (amenityValidationErrors.value.length > 0) {
                    // Don't show generic toast for validation errors, they'll be displayed inline
                    throw error
                }
            }

            app.$toast.error(t('room_types.error_saving_sustainable_amenities'))
            // Throw error to prevent the main save flow from continuing
            throw error
        }
    }

    const saveZeroAmenities = async (roomId: string | number): Promise<void> => {
        try {
            // Transform zero amenities data based on user selections
            const amenitiesPayload = sustainableAmenitiesData.value.map(amenity => {
                const amenityKey = amenity.amenityTypeId || amenity.id
                let isEnabled = false
                let extraEnabled = false

                if (amenity.basicEnabled === true) {
                    // For basicEnabled = true amenities (first block): checked = disabled (false), unchecked = enabled (true)
                    const isChecked = formData.value.zeroAmenitiesEnabled?.[amenityKey] || false
                    isEnabled = !isChecked
                    // For first section ZERO rooms: extraEnabled should be opposite of enabled
                    // If manager removed amenity (enabled = false), traveler can add it (extraEnabled = true)
                    // If manager kept amenity (enabled = true), traveler cannot add it again (extraEnabled = false)
                    extraEnabled = !isEnabled
                } else {
                    // For basicEnabled = false amenities (second block): checked = enabled (true), unchecked = disabled (false)
                    extraEnabled = formData.value.zeroAmenitiesExtraEnabled?.[amenityKey] || false
                }

                // Ensure all amenities with energy-related fields have proper energy type and kW consumption
                let energyType = getAmenityValue(amenityKey, 'energyType') || amenity.energyType
                let kwconsumption = getAmenityValue(amenityKey, 'kwconsumption') || amenity.kwconsumption

                // If amenity has energy-related fields but no energy type, set defaults
                if (amenity.kwconsumption !== null && !energyType) {
                    energyType = 'ELECTRICAL'
                    kwconsumption = getSafeKwConsumption(amenity.amenityTypeId || amenity.id)
                }

                return {
                    amenityTypeId: amenity.amenityTypeId,
                    category: amenity.category,
                    enabled: isEnabled,
                    // Set basicEnabled based on whether the amenity was enabled in the basic room
                    // This should reflect the original enabled state from the basic room, not the current enabled state
                    basicEnabled: amenity.basicEnabled || false,
                    extraEnabled: extraEnabled,
                    usageHours: convertToNumber(getAmenityValue(amenityKey, 'usageHours')),
                    kwconsumption: convertToNumber(kwconsumption),
                    energyType: energyType || null,
                    price: convertToNumber(getAmenityValue(amenityKey, 'price') || amenity.price),
                    name: amenity.name,
                }
            })

            await app.$api.hotelRoomAmenities.post(
                {
                    productId: supplierStore.fullContract.id,
                    hotelId: supplierStore.hotelInfo.hotelId,
                    roomId: roomId,
                },
                amenitiesPayload
            )
        } catch (error: any) {
            console.error('Error saving zero amenities:', error)

            // Handle validation errors from server
            if (error.errors && Array.isArray(error.errors)) {
                amenityValidationErrors.value = error.errors.filter((e: any) => e.errorType === 'validation')
                if (amenityValidationErrors.value.length > 0) {
                    // Don't show generic toast for validation errors, they'll be displayed inline
                    throw error
                }
            }

            app.$toast.error(t('room_types.error_saving_zero_amenities'))
            // Throw error to prevent the main save flow from continuing
            throw error
        }
    }

    const isLoadingChannelManagersConfig = ref(false)
    const channelManagersConfigItems = ref([])
    // Guards the save payload: only a list that came back from the API may be sent back.
    const isChannelManagersConfigLoaded = ref(false)

    const loadChannelManagersConfig = async () => {
        const roomId = formData.value?.id
        if (!roomId) return
        isLoadingChannelManagersConfig.value = true
        try {
            const data = await app.$api.channelManagersConfig.getForRoom(roomId)
            channelManagersConfigItems.value = data ? clone(data) : []
            isChannelManagersConfigLoaded.value = true
        } catch (e) {
            console.error('Failed to load room channel managers config:', e)
            channelManagersConfigItems.value = []
            isChannelManagersConfigLoaded.value = false
            // Tell the user the block is unusable instead of showing an empty "no data" list,
            // which reads as "this room has no channel managers".
            app.$toast.error(
                t(
                    'error_message.load_channel_managers_error',
                    'Could not load the Channel Managers of this room. Their settings are left unchanged on save.'
                )
            )
        } finally {
            isLoadingChannelManagersConfig.value = false
        }
    }

    onMounted(() => {
        if (formData.value?.id && formData.value?.sustainabilityLevel === 'BASIC') {
            loadChannelManagersConfig()
        }
    })
</script>
